import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {RefundService, RefundLineItem} from '../../../_services/refund.service';

interface RefundLineRow {
  lineItemId: string;
  name: string;
  unitPrice: number;     // cents per unit
  originalQty: number;
  refundQty: number;
  refundAmount: number;  // cents
  selected: boolean;
}

/**
 * Refund modal. Caller passes the order; user picks full vs per-line and
 * we POST /api/orders/refund. Loading/success/failure states are handled
 * locally so the parent component doesn't have to micromanage them.
 */
@Component({
  selector: 'app-refund-order-dialog',
  templateUrl: './refund-order-dialog.component.html',
  styleUrls: ['./refund-order-dialog.component.scss']
})
export class RefundOrderDialogComponent implements OnInit {
  order: any;
  mode: 'full' | 'partial' = 'full';
  reason = '';
  lines: RefundLineRow[] = [];

  loading = false;
  errorMessage = '';
  successMessage = '';

  constructor(
    private dialogRef: MatDialogRef<RefundOrderDialogComponent>,
    private refundService: RefundService,
    @Inject(MAT_DIALOG_DATA) data: any
  ) {
    this.order = data && data.order;
  }

  ngOnInit(): void {
    const lineItems = (this.order && this.order.lineItems) || [];
    this.lines = lineItems
      .filter((li: any) => !li.refunded)
      .map((li: any) => {
        const unit = Number(li.priceWithModifiers || li.price || 0);
        const qty = Number(li.quantity || 1);
        return {
          lineItemId: li.id,
          name: li.name || 'Item',
          unitPrice: unit,
          originalQty: qty,
          refundQty: qty,
          refundAmount: unit * qty,
          selected: false,
        };
      });
  }

  setMode(mode: 'full' | 'partial'): void {
    this.mode = mode;
    this.errorMessage = '';
    this.confirmBeforeSubmit = false;
  }

  toggleLine(line: RefundLineRow): void {
    line.selected = !line.selected;
  }

  onQtyChange(line: RefundLineRow): void {
    if (!Number.isFinite(line.refundQty) || line.refundQty < 0) {
      line.refundQty = 0;
    }
    if (line.refundQty > line.originalQty) {
      line.refundQty = line.originalQty;
    }
    // Default the amount to qty * unitPrice on each qty change, but
    // leave the user free to type in a different amount manually
    // afterwards (Clover device flow allows partial value refunds).
    line.refundAmount = line.refundQty * line.unitPrice;
  }

  onAmountChange(line: RefundLineRow, dollarValue: string): void {
    // The dollar input is decimal-formatted; convert to cents on the way
    // out and clamp to the line's max original amount.
    const cents = Math.round(Number(dollarValue || 0) * 100);
    const cap = line.originalQty * line.unitPrice;
    line.refundAmount = Number.isFinite(cents) ? Math.max(0, Math.min(cents, cap)) : 0;
  }

  totalRefundCents(): number {
    if (this.mode === 'full') {
      return Number(this.order && this.order.total) || this.lines.reduce((s, l) => s + l.refundAmount, 0);
    }
    return this.lines.filter(l => l.selected).reduce((s, l) => s + l.refundAmount, 0);
  }

  formatDollars(cents: number): string {
    return '$' + ((cents || 0) / 100).toFixed(2);
  }

  canSubmit(): boolean {
    if (this.loading) { return false; }
    if (this.mode === 'full') { return this.totalRefundCents() > 0; }
    const sel = this.lines.filter(l => l.selected);
    if (sel.length === 0) { return false; }
    return sel.every(l => l.refundAmount > 0 && l.refundQty > 0);
  }

  confirmBeforeSubmit = false;

  submit(): void {
    if (!this.canSubmit()) { return; }
    if (!this.confirmBeforeSubmit) {
      this.confirmBeforeSubmit = true;
      return;
    }
    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    const payload = this.mode === 'full'
      ? {
          orderId: this.order.id,
          fullRefund: true,
          reason: this.reason || undefined,
        }
      : {
          orderId: this.order.id,
          fullRefund: false,
          lineItems: this.lines.filter(l => l.selected).map<RefundLineItem>(l => ({
            lineItemId: l.lineItemId,
            quantity: l.refundQty,
            amount: l.refundAmount,
          })),
          reason: this.reason || undefined,
        };

    this.refundService.refund(payload).subscribe(
      result => {
        this.loading = false;
        this.successMessage = `Refund processed (${result?.paymentState || 'updated'}).`;
        // Hold the success message visible briefly so the user sees the
        // confirmation before the modal closes and the orders list refreshes.
        setTimeout(() => this.dialogRef.close({success: true, result}), 900);
      },
      err => {
        this.loading = false;
        const raw = (err && err.error && err.error.message) || (typeof err === 'string' ? err : null);
        this.errorMessage = raw || 'Refund failed. Please try again.';
      }
    );
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
